import { GanttTask, GanttConfig, WeekStartDay } from '../types/gantt';
import { getTheme, isLightColor } from '../services/themes';
import {
  calculateTimelineBounds,
  generateHeaderColumns,
  daysBetween,
  formatDateToIso,
  formatDayMonth,
} from './dateUtils';

export interface SvgRenderResult {
  svgString: string;
  width: number;
  height: number;
  dataUrl: string;
}

export function generateGanttSvg(
  tasks: GanttTask[],
  config: GanttConfig
): SvgRenderResult {
  const theme = getTheme(config.themeId, config.customColor);
  const weekStartDay = config.weekStartDay || 'monday';
  const showWeekNumbers = config.showWeekNumbers !== false;
  const bounds = calculateTimelineBounds(tasks, config.timeScale, weekStartDay);
  const { primaryHeaders, secondaryHeaders } = generateHeaderColumns(bounds, {
    showWeekNumbers,
    weekStartDay,
  });

  // Layout metrics
  const labelColWidth = 220; // Width reserved on left for Activity names
  const rowHeight = config.rowHeight || 44;
  const barHeight = Math.round(rowHeight * 0.55);
  const barPaddingY = (rowHeight - barHeight) / 2;

  const headerPrimaryHeight = 28;
  const headerSecondaryHeight = 28;
  const totalHeaderHeight = headerPrimaryHeight + headerSecondaryHeight;

  const todayIso = formatDateToIso(new Date());
  const isTodayVisible = config.showToday && todayIso >= bounds.startDate && todayIso <= bounds.endDate;
  const titleHeight = config.title ? 48 : (isTodayVisible ? 28 : 0);
  const bottomPadding = 20;

  // Auto-adjust min chart width based on the scale and number of columns
  let minChartAreaWidth = 650;
  if (bounds.scale === 'days') {
    minChartAreaWidth = Math.max(650, bounds.totalDays * 24);
  } else if (bounds.scale === 'weeks') {
    minChartAreaWidth = Math.max(650, (bounds.totalDays / 7) * 50);
  } else if (bounds.scale === 'months') {
    minChartAreaWidth = Math.max(650, secondaryHeaders.length * 68);
  } else {
    minChartAreaWidth = Math.max(650, secondaryHeaders.length * 90);
  }

  const chartAreaWidth = Math.max(minChartAreaWidth, config.chartWidth || 800);
  const totalWidth = labelColWidth + chartAreaWidth;
  const chartBodyHeight = Math.max(1, tasks.length) * rowHeight;
  const totalHeight = titleHeight + totalHeaderHeight + chartBodyHeight + bottomPadding;

  const dayWidth = chartAreaWidth / bounds.totalDays;

  // XML builder
  const svgParts: string[] = [];

  svgParts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalWidth} ${totalHeight}" width="${totalWidth}" height="${totalHeight}" style="background-color:${theme.background}; font-family:'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;">`
  );

  // Background rect
  svgParts.push(
    `<rect width="${totalWidth}" height="${totalHeight}" fill="${theme.background}" rx="8"/>`
  );

  // Title
  if (config.title) {
    svgParts.push(
      `<text x="${labelColWidth > 0 ? 24 : 16}" y="32" font-size="18" font-weight="700" fill="${theme.text}">${escapeXml(config.title)}</text>`
    );
  }

  const chartTopY = titleHeight;

  // Header background
  if (chartTopY === 0) {
    // Round top corners of header if no title banner above it
    svgParts.push(
      `<rect x="0" y="0" width="${totalWidth}" height="${totalHeaderHeight}" fill="${theme.headerBg}" rx="8" />`
    );
    svgParts.push(
      `<rect x="0" y="${totalHeaderHeight / 2}" width="${totalWidth}" height="${totalHeaderHeight / 2}" fill="${theme.headerBg}" />`
    );
  } else {
    svgParts.push(
      `<rect x="0" y="${chartTopY}" width="${totalWidth}" height="${totalHeaderHeight}" fill="${theme.headerBg}" />`
    );
  }

  // Activity Header label (top left corner)
  svgParts.push(
    `<rect x="0" y="${chartTopY}" width="${labelColWidth}" height="${totalHeaderHeight}" fill="none" stroke="${theme.grid}" stroke-width="0.5" opacity="0.3" />`
  );
  svgParts.push(
    `<text x="24" y="${chartTopY + totalHeaderHeight / 2 + 5}" font-size="13" font-weight="600" fill="${theme.headerText}">Activity</text>`
  );

  // Render Primary Headers (Months / Years)
  primaryHeaders.forEach((col) => {
    const colX = labelColWidth + col.startDayIndex * dayWidth;
    const colW = col.spanDays * dayWidth;

    svgParts.push(
      `<rect x="${colX}" y="${chartTopY}" width="${colW}" height="${headerPrimaryHeight}" fill="none" stroke="${theme.grid}" stroke-width="0.5" opacity="0.3" />`
    );
    svgParts.push(
      `<text x="${colX + colW / 2}" y="${chartTopY + 18}" text-anchor="middle" font-size="12" font-weight="600" fill="${theme.headerText}">${escapeXml(col.label)}</text>`
    );
  });

  // Render Secondary Headers (Days / Weeks / Months / Quarters)
  secondaryHeaders.forEach((col) => {
    const colX = labelColWidth + col.startDayIndex * dayWidth;
    const colW = col.spanDays * dayWidth;
    const secY = chartTopY + headerPrimaryHeight;

    svgParts.push(
      `<rect x="${colX}" y="${secY}" width="${colW}" height="${headerSecondaryHeight}" fill="none" stroke="${theme.grid}" stroke-width="0.5" opacity="0.3" />`
    );

    if (col.subLabel) {
      // 2-line header (e.g. Day number + Weekday, or W1 + start day)
      svgParts.push(
        `<text x="${colX + colW / 2}" y="${secY + 13}" text-anchor="middle" font-size="10" font-weight="600" fill="${theme.headerText}">${escapeXml(col.label)}</text>`
      );
      svgParts.push(
        `<text x="${colX + colW / 2}" y="${secY + 23}" text-anchor="middle" font-size="9" font-weight="400" fill="${theme.headerText}" opacity="0.85">${escapeXml(col.subLabel)}</text>`
      );
    } else {
      // 1-line centered header (e.g. Month or Quarter)
      svgParts.push(
        `<text x="${colX + colW / 2}" y="${secY + 18}" text-anchor="middle" font-size="11" font-weight="600" fill="${theme.headerText}">${escapeXml(col.label)}</text>`
      );
    }
  });

  // Left column divider
  svgParts.push(
    `<line x1="${labelColWidth}" y1="${chartTopY}" x2="${labelColWidth}" y2="${chartTopY + totalHeaderHeight + chartBodyHeight}" stroke="${theme.grid}" stroke-width="1.5" />`
  );

  // Background Gridlines & Alternating Row Fills
  const bodyTopY = chartTopY + totalHeaderHeight;
  const showBandedRows = config.showBandedRows !== false;

  tasks.forEach((_, idx) => {
    const rowY = bodyTopY + idx * rowHeight;
    // Alternating row background
    if (showBandedRows && idx % 2 === 1) {
      svgParts.push(
        `<rect x="0" y="${rowY}" width="${totalWidth}" height="${rowHeight}" fill="${theme.surface}" opacity="0.5" />`
      );
    }
    // Horizontal row border
    svgParts.push(
      `<line x1="0" y1="${rowY + rowHeight}" x2="${totalWidth}" y2="${rowY + rowHeight}" stroke="${theme.grid}" stroke-width="1" />`
    );
  });

  // Vertical Gridlines across chart body
  if (config.showGridlines) {
    secondaryHeaders.forEach((col) => {
      const colX = labelColWidth + col.startDayIndex * dayWidth;
      svgParts.push(
        `<line x1="${colX}" y1="${bodyTopY}" x2="${colX}" y2="${bodyTopY + chartBodyHeight}" stroke="${theme.grid}" stroke-width="1" stroke-dasharray="2 2" />`
      );
    });
    // Final right border
    svgParts.push(
      `<line x1="${totalWidth}" y1="${bodyTopY}" x2="${totalWidth}" y2="${bodyTopY + chartBodyHeight}" stroke="${theme.grid}" stroke-width="1" stroke-dasharray="2 2" />`
    );
  }

  // Render Tasks & Bars
  tasks.forEach((task, idx) => {
    const rowY = bodyTopY + idx * rowHeight;
    const taskStartIso = task.start || bounds.startDate;
    const taskEndIso = task.end || task.start || bounds.startDate;

    const startOffsetDays = Math.max(0, daysBetween(bounds.startDate, taskStartIso));
    const durationDays = Math.max(1, daysBetween(taskStartIso, taskEndIso) + 1);

    const barX = labelColWidth + startOffsetDays * dayWidth;
    const barW = Math.max(12, durationDays * dayWidth);
    const barY = rowY + barPaddingY;

    // Activity Label Text on Left Column
    svgParts.push(
      `<text x="24" y="${rowY + rowHeight / 2 + 5}" font-size="13" font-weight="500" fill="${theme.text}">${escapeXml(task.activity)}</text>`
    );

    const isLegacyBlue =
      task.color &&
      (task.color.toUpperCase() === '#0078D4' ||
        task.color.toUpperCase() === '#2B88D8' ||
        task.color.toUpperCase() === '#106EBE' ||
        task.color.toUpperCase() === '#005A9E' ||
        task.color.toUpperCase() === '#2563EB' ||
        task.color.toUpperCase() === '#0284C7');
    const barColor = !isLegacyBlue && task.color ? task.color : theme.primary;
    const isMilestone = task.start === task.end || durationDays <= 1;
    const rowBg = (showBandedRows && idx % 2 === 1) ? theme.surface : theme.background;

    if (isMilestone && task.start === task.end) {
      // Milestone diamond
      const diamondSize = 16;
      const centerX = barX + dayWidth / 2;
      const centerY = rowY + Math.round(rowHeight * 0.38);

      svgParts.push(
        `<polygon points="${centerX},${centerY - diamondSize / 2} ${centerX + diamondSize / 2},${centerY} ${centerX},${centerY + diamondSize / 2} ${centerX - diamondSize / 2},${centerY}" fill="${theme.primary}" stroke="${rowBg}" stroke-width="1.5" />`
      );

      // Milestone date text positioned cleanly below the marker and kept within chart bounds
      const dateText = formatShortDate(task.start, weekStartDay);
      const textY = rowY + rowHeight - 6;
      const approxHalfWidth = Math.ceil((dateText.length * 6.2) / 2);

      let textX = centerX;
      let textAnchor = 'middle';

      if (centerX + approxHalfWidth > totalWidth - 8) {
        textX = totalWidth - 8;
        textAnchor = 'end';
      } else if (centerX - approxHalfWidth < labelColWidth + 8) {
        textX = labelColWidth + 8;
        textAnchor = 'start';
      }

      svgParts.push(
        `<text x="${textX}" y="${textY}" text-anchor="${textAnchor}" font-size="9.5" font-weight="600" fill="${theme.text}">${dateText}</text>`
      );
    } else {
      const hasProgress = config.showProgress && task.progress !== undefined && task.progress < 100;
      const clipId = `bar-clip-${idx}`;

      // Clip path for progress overlay to fit exactly within rounded corners
      if (hasProgress) {
        svgParts.push(
          `<defs><clipPath id="${clipId}"><rect x="${barX}" y="${barY}" width="${barW}" height="${barHeight}" rx="${theme.barRadius}" /></clipPath></defs>`
        );
      }

      // Base bar rect (solid rich theme color matching header text boxes)
      svgParts.push(
        `<rect x="${barX}" y="${barY}" width="${barW}" height="${barHeight}" rx="${theme.barRadius}" fill="${barColor}" />`
      );

      // Smart contrast calculation for bar text and progress track
      const isBarLight = isLightColor(barColor);
      const barTextColor = isBarLight ? '#201F1E' : '#FFFFFF';
      const progressTrackColor = isBarLight ? '#201F1E' : '#FFFFFF';

      // Progress indicator (subtle progress line at bottom of bar to avoid two-tone clashing)
      if (hasProgress && task.progress! > 0) {
        const progressW = (barW * Math.max(0, Math.min(100, task.progress!))) / 100;
        svgParts.push(
          `<rect x="${barX + 2}" y="${barY + barHeight - 4}" width="${Math.max(4, progressW - 4)}" height="2.5" rx="1.2" fill="${progressTrackColor}" opacity="0.85" clip-path="url(#${clipId})" />`
        );
      }

      // Bar text label (Activity name / duration)
      if (barW > 60) {
        svgParts.push(
          `<text x="${barX + 10}" y="${barY + barHeight / 2 + 4}" font-size="11" font-weight="600" fill="${barTextColor}">${escapeXml(task.activity)} (${durationDays}d)</text>`
        );
      } else {
        // If bar is too narrow, put label next to it
        svgParts.push(
          `<text x="${barX + barW + 8}" y="${barY + barHeight / 2 + 4}" font-size="11" font-weight="500" fill="${theme.text}">${durationDays}d</text>`
        );
      }
    }
  });

  // "Today" marker line & badge above the table
  if (isTodayVisible) {
    const todayOffsetDays = daysBetween(bounds.startDate, todayIso);
    const todayX = labelColWidth + todayOffsetDays * dayWidth + dayWidth / 2;

    // Vertical line through table
    svgParts.push(
      `<line x1="${todayX}" y1="${chartTopY}" x2="${todayX}" y2="${bodyTopY + chartBodyHeight}" stroke="${theme.todayLine}" stroke-width="2" stroke-dasharray="4 3" />`
    );

    // Today Pill label positioned above the table
    const pillW = 46;
    const pillH = 18;
    const pillY = chartTopY - pillH - 3;
    const pillX = Math.round(todayX - pillW / 2);

    svgParts.push(
      `<rect x="${pillX}" y="${pillY}" width="${pillW}" height="${pillH}" rx="4" fill="${theme.todayLine}" />`
    );
    svgParts.push(
      `<polygon points="${todayX - 3},${chartTopY - 3} ${todayX + 3},${chartTopY - 3} ${todayX},${chartTopY}" fill="${theme.todayLine}" />`
    );
    svgParts.push(
      `<text x="${todayX}" y="${pillY + 12.5}" text-anchor="middle" font-size="9.5" font-weight="700" fill="#FFFFFF" letter-spacing="0.5">TODAY</text>`
    );
  }

  svgParts.push('</svg>');

  const svgString = svgParts.join('\n');
  const base64 = btoa(unescape(encodeURIComponent(svgString)));
  const dataUrl = `data:image/svg+xml;base64,${base64}`;

  return {
    svgString,
    width: totalWidth,
    height: totalHeight,
    dataUrl,
  };
}

function escapeXml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function formatShortDate(iso: string, weekStartDay: WeekStartDay = 'monday'): string {
  return formatDayMonth(iso, weekStartDay);
}
